import { Form, FormItem, useForm } from '@/commons/components/Form';
import TagsField from '@/commons/components/Form/TagsField';
import { getStorages } from '@/redux/selectors/storage.selector';
import { Dispatch, RootState } from '@/redux/store';
import { FileModel, FileType } from '@/types';
import { Button, Col, Collapse, Image, Input, Pagination, Row, Skeleton, Tabs } from 'antd';
import cx from 'classnames';
import update from 'immutability-helper';
import { FC, useCallback, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import FileItem, { FileItemDraggable } from './FileItem';

import './styles.scss';

const TABS = {
  list: 'list',
  selected: 'selected',
};

const FilterForm = ({ onFinish }) => {
  const [form] = useForm();
  const criteria = useSelector((state: RootState) => state.storage.criteria);
  useEffect(() => {
    form.setFieldsValue({ ...criteria });
  }, [criteria]);

  return (
    <Form form={form} onFinish={onFinish}>
      <Row gutter={16}>
        <Col span="8">
          <FormItem name="keyword">
            <Input placeholder="Name|Code" />
          </FormItem>
        </Col>
        <Col span="8">
          <FormItem name="tags">
            <TagsField placeholder="tags" />
          </FormItem>
        </Col>
        <Col span="24" style={{ textAlign: 'right' }}>
          <FormItem>
            <Button type="primary" htmlType="submit" style={{ marginRight: 12 }}>
              Search
            </Button>
            <Button type="default" onClick={() => form.resetFields()}>
              Clear
            </Button>
          </FormItem>
        </Col>
      </Row>
    </Form>
  );
};
type ImageListDraggableProps = {
  onUpdateContent?: (value: FileModel[]) => void;
} & ImageListProps;
const ImageListDraggable: FC<ImageListDraggableProps> = (props: ImageListDraggableProps) => {
  const {
    content,
    selectedItems,
    onSelectedItem,
    onRemoveItem,
    multiple = true,
    scrollable = true,
    onDeleteItem,
    onUpdateContent,
    onSearch,
  } = props;
  const [preview, setPreview] = useState<boolean>(false);
  const contentMapped: FileModel[] = useSelector(
    createSelector(
      (state: RootState) => state.storageEntities.ids,
      (entities) => content.map((item) => entities[item.id] || item).filter((item) => !!item) || []
    )
  );
  const findItem = useCallback(
    (id: number) => {
      const targetItem = content.filter((c) => c.id === id)[0];
      return {
        item: targetItem,
        index: content.indexOf(targetItem),
      };
    },
    [content]
  );
  const moveItem = useCallback(
    (id, atIndex) => {
      const { item, index } = findItem(id);

      if (onUpdateContent) {
        onUpdateContent(
          update(content, {
            $splice: [
              [index, 1],
              [atIndex, 0, item],
            ],
          })
        );
      }
    },
    [findItem, content, onUpdateContent]
  );
  return (
    <DndProvider backend={HTML5Backend}>
      <Image.PreviewGroup
        preview={{
          visible: preview,
          onVisibleChange: (vs) => {
            if (!vs) setPreview(vs);
          },
        }}
      >
        <Row
          gutter={[30, 16]}
          className={cx('uploaded-image-list', { 'uploaded-image-list--scrollable': scrollable })}
          style={{ marginRight: 0 }}
        >
          {contentMapped &&
            !!contentMapped.length &&
            contentMapped.map((item) => {
              return (
                <FileItemDraggable
                  key={item.id}
                  item={item}
                  findItem={findItem}
                  moveItem={moveItem}
                  onPreview={setPreview}
                  selectedItems={selectedItems}
                  multiple={multiple}
                  onCheckItem={onSelectedItem}
                  onDeleteItem={onDeleteItem}
                  onRemoveItem={onRemoveItem}
                  onSearch={onSearch}
                />
              );
            })}
        </Row>
      </Image.PreviewGroup>
    </DndProvider>
  );
};
type ImageListProps = {
  content: FileModel[];
  selectedItems?: FileModel[];
  onSelectedItem?: (item: FileModel, checked: boolean) => void;
  onRemoveItem?: (item: FileModel) => void;
  multiple?: boolean;
  scrollable?: boolean;
  onDeleteItem?: (item: FileModel) => void;
  onSearch?: (values: any) => void;
};
const ImageList: FC<ImageListProps> = (props: ImageListProps) => {
  const {
    content,
    selectedItems,
    onSelectedItem,
    onRemoveItem,
    multiple = true,
    scrollable = true,
    onDeleteItem,
    onSearch,
  } = props;
  const [preview, setPreview] = useState<boolean>(false);
  const contentMapped: FileModel[] = useSelector(
    createSelector(
      (state: RootState) => state.storageEntities.ids,
      (entities) => content.map((item) => entities[item.id] || item).filter((item) => !!item) || []
    )
  );
  return (
    <Image.PreviewGroup
      preview={{
        visible: preview,
        onVisibleChange: (vs) => {
          if (!vs) setPreview(vs);
        },
      }}
    >
      <Row
        gutter={[30, 16]}
        className={cx('uploaded-image-list', { 'uploaded-image-list--scrollable': scrollable })}
        style={{ marginRight: 0 }}
      >
        {contentMapped &&
          !!contentMapped.length &&
          contentMapped.map((item) => {
            return (
              <FileItem
                key={item.id}
                item={item}
                onPreview={setPreview}
                selectedItems={selectedItems}
                multiple={multiple}
                onCheckItem={onSelectedItem}
                onDeleteItem={onDeleteItem}
                onRemoveItem={onRemoveItem}
                onSearch={onSearch}
              />
            );
          })}
      </Row>
    </Image.PreviewGroup>
  );
};
type Props = {
  selectedItems?: FileModel[];
  onSelectItem?: (item: FileModel, checked: boolean) => void;
  onUpdateSelectedItems?: (value: FileModel[]) => void;
  isRefresh?: boolean;
  contentTypes?: FileType[];
  onDeleteItem?: (item: FileModel) => void;
};
const UploadedFileContent: FC<Props> = (props: Props) => {
  const {
    selectedItems,
    onSelectItem,
    isRefresh = false,
    contentTypes,
    onDeleteItem,
    onUpdateSelectedItems,
  } = props;
  const [activeKey, setActiveKey] = useState<string>(TABS.list);
  const dispatch = useDispatch<Dispatch>();
  const pagination = useSelector((state: RootState) => state.storage.pagination);
  const criteria = useSelector((state: RootState) => state.storage.criteria);
  const loading = useSelector((state: RootState) => state.storage.loading);
  const { size = 20, totalElements = 0, number = 0, totalPages = 0 } = pagination;
  const content: FileModel[] = useSelector(getStorages);

  useEffect(() => {
    if (isRefresh) {
      dispatch.storage.filterAsync({
        ...pagination,
        page: 0,
        contentTypes,
      });
    }
  }, [isRefresh]);

  const handlePaging = (page: number, pageSize?: number) => {
    dispatch.storage.filterAsync({
      ...pagination,
      ...criteria,
      contentTypes,
      page: page - 1,
      size: pageSize,
    });
  };
  const handleSearch = (values) => {
    dispatch.storage.filterAsync({
      ...pagination,
      contentTypes,
      originalName: values.keyword,
      ...values,
    });
  };

  const handleUpdateActiveKey = (key: string) => {
    setActiveKey(key);
  };

  return (
    <Row>
      <Col span={24}>
        <Row>
          <Col span={24}>
            <Collapse
              items={[
                {
                  key: '1',
                  label: 'Filter',
                  children: <FilterForm onFinish={handleSearch} />,
                },
              ]}
            />
          </Col>
          <Col span={24}>
            <Tabs
              onChange={handleUpdateActiveKey}
              activeKey={activeKey}
              items={[
                {
                  key: TABS.list,
                  label: 'List',
                  children: !loading ? (
                    <ImageList
                      content={content}
                      selectedItems={selectedItems}
                      onSelectedItem={onSelectItem}
                      onDeleteItem={onDeleteItem}
                      onSearch={handleSearch}
                    />
                  ) : (
                    <Skeleton active />
                  ),
                },
                {
                  key: TABS.selected,
                  disabled: !!selectedItems,
                  label: `Selected (${selectedItems?.length || 0})`,
                  children: (
                    <ImageListDraggable
                      content={selectedItems || []}
                      selectedItems={selectedItems}
                      onSelectedItem={onSelectItem}
                      onUpdateContent={onUpdateSelectedItems}
                    />
                  ),
                },
              ]}
            />
          </Col>
        </Row>
      </Col>
      <Col span={24} style={{ display: 'flex', justifyContent: 'center', marginTop: '1.6rem' }}>
        {pagination && totalPages > 1 && activeKey === TABS.list && (
          <Pagination
            current={number + 1}
            onChange={handlePaging}
            total={totalElements}
            pageSize={size}
          />
        )}
      </Col>
    </Row>
  );
};

export { ImageList, ImageListDraggable };
export default UploadedFileContent;
